import type { ReactNode } from 'react';
import { useSurveyI18n, type SurveyStrings } from '../i18n';
import { LIVE_COLOR } from '../sections';
import { inBoth, seasonBadge, type Figures, type Season } from '../seasons';
import { fmtDay, fmtDayRange, fmtDec, fmtDuration, fmtHours, fmtInt, fmtPct, ratio, share } from '../utils';

/*
 * The headline cards. Each shows the figures of the picked scope (both
 * seasons = the campaign, a member counted once; or one season) and, beside
 * them, the seasons in that scope one column each. A season still to come
 * reads "—", never 0.
 */

type CardProps = {
  /** The scope's figures: the campaign's or one season's. */
  figures: Figures;
  /** The seasons in the scope (both, or the one picked). */
  seasons: Season[];
};

/** A season's column heading: colour, name and a detail ("6 Oct – 16 Nov", "412 318 tickets"). */
export function SeasonHead({ season, detail }: { season: Season; detail?: ReactNode }) {
  const { t } = useSurveyI18n();
  return (
    <div className="su-shead">
      <span className="su-dot" style={{ background: season.color }} />
      {t.season(season.n)}
      {detail != null && <span className="su-sdates">{detail}</span>}
    </div>
  );
}

/** "Season 1 412 318 · Season 2 168 400" under a figure, while both seasons are on show. */
export function SeasonSplit({ seasons, value }: { seasons: Season[]; value: (s: Season) => string }) {
  const { t } = useSurveyI18n();
  if (seasons.length < 2) return null;
  return (
    <div className="su-season-split">
      {seasons.map((s) => (
        <span key={s.id}>
          <span className="su-dot" style={{ background: s.color }} />
          {t.season(s.n)} <strong>{s.status === 'planned' ? '—' : value(s)}</strong>
        </span>
      ))}
    </div>
  );
}

const badgeClass = (s: Season) => (s.status === 'live' ? 'live' : s.status === 'done' ? 'ended' : 'planned');

/** Unique visitors and the funnel to playing, per season: visitors → opt-ins → started a quiz → players. */
export function VisitorsFunnel({ figures, seasons }: CardProps) {
  const { t } = useSurveyI18n();
  const all = seasons.length > 1;
  const both = all ? inBoth(seasons, figures.uniqueVisitors, (f) => f.uniqueVisitors) : null;
  return (
    <section id="visitors" className="su-card">
      <div className="su-hero-head">
        <h2 className="su-eyebrow">{t.uniqueVisitorsEyebrow}</h2>
        {seasons.map((s) => (
          <span key={s.id} className={`su-badge ${badgeClass(s)}`}>
            {seasonBadge(s, t, (iso) => fmtDay(iso, t.locale, 'dayMonth'))}
          </span>
        ))}
      </div>
      <div className="su-hero-figure">
        <div className="su-huge">{fmtInt(figures.uniqueVisitors)}</div>
        <span className="su-note">{all ? t.funnelNoteAll(both == null ? null : fmtInt(both)) : t.funnelNote}</span>
      </div>
      <div className="su-col2">
        {seasons.map((s) => (
          <div key={s.id}>
            <SeasonHead season={s} detail={fmtDayRange(s.start, s.end, t.locale)} />
            <Funnel season={s} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Funnel({ season }: { season: Season }) {
  const { t } = useSurveyI18n();
  const f = season.figures;
  const base = f.uniqueVisitors;
  const steps = [
    { label: t.uniqueVisitors, value: base, color: '#065A8E' },
    { label: t.optIns, value: f.optIns, color: '#0F75B8' },
    // Players who opened a quiz — shown once the pipeline publishes the started counters.
    ...(f.playersStarted != null || season.status === 'planned' ? [{ label: t.startedAQuiz, value: f.playersStarted, color: '#2A9CC4' }] : []),
    { label: t.uniquePlayers, value: f.uniquePlayers, color: '#3BB8C9' },
  ];
  return (
    <div className="su-funnel">
      {steps.map((step) => {
        const pct = step.value === base && base != null && base > 0 ? 100 : share(step.value, base);
        return (
          <div className="su-funnel-step" key={step.label}>
            <div className="su-funnel-head">
              <span>{step.label}</span>
              <span>{season.status === 'planned' ? t.funnelNotStarted : `${fmtInt(step.value)} · ${fmtPct(pct, pct === 100 ? 0 : 1)}`}</span>
            </div>
            <div className="su-track" data-tip={step.value == null ? undefined : t.funnelTip(step.label, fmtInt(step.value), fmtPct(pct))}>
              {pct != null && <div className="su-fill" style={{ width: `${pct}%`, background: step.color }} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** "S2 Week 4 in progress (shown in red)", or that the campaign is over. */
function liveNote(seasons: Season[], t: SurveyStrings): string | null {
  const live = seasons.filter((s) => s.liveWeek);
  const labels = live.map((s) => `${t.seasonShort(s.n)} ${t.week(s.weeks.indexOf(s.liveWeek!) + 1)}`);
  if (labels.length > 0) return t.weekInProgressNote(labels.join(t.and));
  return seasons.length > 0 && seasons.every((s) => s.status === 'done') ? t.allWeeksComplete : null;
}

/** Lottery tickets issued: the scope's total, tickets per player, and each season's weeks. */
export function TicketsCard({ figures, seasons }: CardProps) {
  const { t } = useSurveyI18n();
  const max = Math.max(1, ...seasons.flatMap((s) => s.weeks.map((w) => w.ticketsIssued ?? 0)));
  const note = liveNote(seasons, t);
  return (
    <section id="tickets" className="su-card">
      <h2 className="su-eyebrow">{t.ticketsEyebrow}</h2>
      <div className="su-stack">
        <div className="su-big">{fmtInt(figures.ticketsIssued)}</div>
        <span className="su-note">{t.ticketsPerPlayer(fmtDec(ratio(figures.ticketsIssued, figures.uniquePlayers)))}</span>
      </div>
      <div className="su-col2">
        {seasons.map((s) => (
          <div key={s.id}>
            <SeasonHead season={s} detail={s.status === 'planned' ? '—' : t.seasonTickets(fmtInt(s.figures.ticketsIssued))} />
            <div className="su-ticket-weeks">
              {s.weeks.map((w, k) => {
                const label = t.week(k + 1);
                const live = w.status === 'live';
                return (
                  <div className="su-ticket-week" key={w.week}>
                    <span className={live ? 'su-live-ink strong' : w.status === 'planned' ? 'su-muted' : ''}>{label}</span>
                    <div
                      className="su-track thin"
                      data-tip={w.ticketsIssued == null ? undefined : t.ticketsTip(`${t.seasonShort(s.n)} · ${label}`, fmtInt(w.ticketsIssued))}
                    >
                      {w.ticketsIssued != null && (
                        <div className="su-fill" style={{ width: `${(w.ticketsIssued / max) * 100}%`, background: live ? LIVE_COLOR : '#065A8E' }} />
                      )}
                    </div>
                    <span>{fmtInt(w.ticketsIssued)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {note && <p className="su-note small">{note}</p>}
    </section>
  );
}

/** The four headline tiles, each with its seasons underneath while both are on show. */
export function KpiRow({ figures, seasons, weeklyPlayLimit }: CardProps & { weeklyPlayLimit: number }) {
  const { t } = useSurveyI18n();
  const avg = (f: Figures) => fmtDuration(ratio(f.timeInAppSec, f.uniquePlayers));
  const tiles = [
    {
      label: t.optIns,
      value: fmtInt(figures.optIns),
      sub: t.ofUniqueVisitors(fmtPct(share(figures.optIns, figures.uniqueVisitors))),
      season: (s: Season) => fmtInt(s.figures.optIns),
    },
    {
      label: t.playsKpi,
      value: fmtInt(figures.plays),
      sub: t.playsPerPlayerMax(fmtDec(ratio(figures.plays, figures.uniquePlayers)), weeklyPlayLimit),
      season: (s: Season) => fmtInt(s.figures.plays),
    },
    { label: t.totalTime, value: fmtHours(figures.timeInAppSec), sub: t.totalTimeSub, season: (s: Season) => fmtHours(s.figures.timeInAppSec) },
    {
      label: t.avgTime,
      value: avg(figures),
      sub: seasons.length === 1 ? t.avgTimeSubSeason(seasons[0].n) : t.avgTimeSub,
      season: (s: Season) => avg(s.figures),
    },
  ];
  return (
    <div id="plays" className="su-kpis">
      {tiles.map((tile) => (
        <div className="su-card su-kpi" key={tile.label}>
          <span className="su-kpi-label">{tile.label}</span>
          <span className="su-kpi-value">{tile.value}</span>
          <span className="su-note">{tile.sub}</span>
          <SeasonSplit seasons={seasons} value={tile.season} />
        </div>
      ))}
    </div>
  );
}

/** Marketing consents: members with a consent, split by channel combination, with each season's share. */
export function ConsentsCard({ figures, seasons }: CardProps) {
  const { t } = useSurveyI18n();
  const c = figures.consents;
  const all = seasons.length > 1;
  const parts = [
    { label: t.emailOnly, value: c?.emailOnly ?? null, color: '#065A8E', light: false },
    { label: t.emailAndSms, value: c?.emailAndSms ?? null, color: '#0A78B8', light: false },
    { label: t.smsOnly, value: c?.smsOnly ?? null, color: '#8ED3DD', light: true },
  ];
  return (
    <section id="consents" className="su-card">
      <h2>{t.consentsTitle}</h2>
      <div className="su-consent-total">
        <strong>{fmtInt(c?.total)}</strong>
        <span className="su-note">{all ? t.totalConsentsAll : t.totalConsents}</span>
      </div>
      <div className="su-stack" style={{ gap: 10 }}>
        <div className="su-split">
          {parts.map((p) => {
            const pct = share(p.value, c?.total) ?? 0;
            return (
              pct > 0 && (
                <div
                  key={p.label}
                  className={p.light ? 'light' : ''}
                  style={{ width: `${pct}%`, background: p.color }}
                  data-tip={t.consentTip(p.label, fmtInt(p.value), fmtPct(pct))}
                  aria-label={`${p.label}: ${fmtPct(pct)}`}
                >
                  {pct >= 6 ? fmtPct(pct) : ''}
                </div>
              )
            );
          })}
        </div>
        <div className="su-legend">
          {parts.map((p) => (
            <span key={p.label}>
              <span className="su-swatch" style={{ background: p.color }} />
              {p.label} · {fmtInt(p.value)}
            </span>
          ))}
        </div>
      </div>
      <div className="su-mini-stats">
        <div className="su-mini">
          <span>{t.emailConsents}</span>
          <strong>{fmtInt(c?.email)}</strong>
          <span>{t.ofOptIns(fmtPct(share(c?.email, figures.optIns)))}</span>
          <SeasonSplit seasons={seasons} value={(s) => fmtInt(s.figures.consents?.email)} />
        </div>
        <div className="su-mini">
          <span>{t.smsConsents}</span>
          <strong>{fmtInt(c?.sms)}</strong>
          <span>{t.ofOptIns(fmtPct(share(c?.sms, figures.optIns)))}</span>
          <SeasonSplit seasons={seasons} value={(s) => fmtInt(s.figures.consents?.sms)} />
        </div>
      </div>
      {all && <p className="su-note small">{t.consentsNote}</p>}
    </section>
  );
}
